import { Collapse, List } from '@mui/material';
import { generateTestId } from '@vs/functions/generate-test-id';
import classNames from 'classnames';
import { Fragment, useState } from 'react';

import MenuListItem from './menu-list-item';

export type MenuItemInterface<TExtended = unknown> = TExtended & {
  name: string;
  url?: string;
  icon?: React.ReactNode | undefined;
  selected?: boolean;
  selectedListButtonClassName?: string;
  underline?: boolean;
  onClick?: () => void;
  hide?: boolean;
  disabled?: boolean;
  childViews?: MenuItemInterface<TExtended>[];
  childViewListClassName?: string;
  ariaLabel?: string;
};

export interface UiPortalMenuProps {
  menuItemList: MenuItemInterface[];
  open: boolean;
  onClick?: (item: MenuItemInterface, isCollapsable: boolean) => void;
  layer?: number;
  canCollapse?: boolean;
  childViewListClassName?: string;
  isAppMenu?: boolean;
}

export function PortalMenu({
  menuItemList,
  open = false,
  onClick: generalOnClick = () => {},
  layer = 1,
  canCollapse = true,
  childViewListClassName = '',
  isAppMenu = false,
}: UiPortalMenuProps) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>(() =>
    menuItemList.reduce<Record<string, boolean>>((obj, item) => {
      if (item.selected) {
        obj[item.name] = true;
      }
      return obj;
    }, {})
  );

  const handleClick = (name: string) => {
    setOpenItems(prevState => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  return (
    <List
      {...generateTestId('menu')}
      role="menu"
      className={classNames(layer === 2 && 'pl-4', childViewListClassName)}
    >
      {menuItemList
        ?.filter(e => !e.hide)
        .filter(e => e.name)
        .map((item, index) => {
          const {
            name,
            icon,
            selected,
            selectedListButtonClassName,
            underline,
            url,
            onClick,
            disabled,
            childViews = [],
          } = item;
          const isOpen = !canCollapse ? true : openItems[name] || false;
          const hasVisibleChildViews =
            childViews.length > 0 && childViews.some(test => !test.hide);
          const allChildViewsVisible =
            childViews.length > 0 && childViews.every(test => !test.hide);

          const isExpandIconShow =
            canCollapse && hasVisibleChildViews && layer === 1;

          const hasSubmenu = allChildViewsVisible && childViews.length > 0;

          return (
            <Fragment key={name}>
              <MenuListItem
                icon={icon}
                label={name}
                showUnderline={underline}
                disabled={disabled}
                selected={selected}
                showExpandIcon={isExpandIconShow}
                isCompactMode={!open}
                isExpand={isOpen}
                onClick={() => {
                  if (typeof onClick === 'function') {
                    allChildViewsVisible && canCollapse
                      ? handleClick(name)
                      : onClick();
                  } else if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                  generalOnClick(item, isExpandIconShow);
                }}
                className={classNames({
                  [selectedListButtonClassName ?? '']: selected,
                })}
                aria-expanded={hasSubmenu ? isOpen : undefined}
                aria-haspopup={hasSubmenu ? 'true' : undefined}
                aria-current={isAppMenu && selected ? 'page' : undefined}
                {...generateTestId('option', [index])}
              />
              {allChildViewsVisible && (
                <Collapse
                  {...generateTestId('option', ['sub-level', index])}
                  in={isOpen}
                  timeout="auto"
                  unmountOnExit
                >
                  <PortalMenu
                    menuItemList={childViews ?? []}
                    open={true}
                    layer={2}
                    childViewListClassName={item.childViewListClassName}
                  />
                </Collapse>
              )}
            </Fragment>
          );
        })}
    </List>
  );
}

export default PortalMenu;
